<script lang="ts" setup>
// TODO Investigate a way to only render report when "Print" button is clicked
//      in order to reduce lag while selecting different report sections.

// TODO Use default units for converter inputs in report
import type {
  ReportSection,
  ScenarioMap,
  SizingReportSections
} from "@/components/types";

import { injectStrict } from "@/utils/utils";
import { computed, reactive, ref, nextTick, watch, provide } from "vue";
import {
  sizingFormKey,
  sizingKey
} from "@/composables/scenarioDetailProvider/scenarioDetailProviderKeys";
import { useScenarioCalculations } from "@/composables/scenarioCalculations/useScenarioCalculations";
import { usePrintReflowFix } from "@/composables/usePrintReflowFix";
import SplitButton from "primevue/splitbutton";
import useDestructureScenario from "@/composables/useDestructureScenario";
import { useToast } from "primevue/usetoast";

import Report from "@/components/reports/Report.vue";
import type { MultiSelectAllChangeEvent } from "primevue/multiselect";
import PumpFrequencyRange from "@/components/scenarios/charts/PumpFrequencyRange.vue";
import LabelledToggle from "@/components/LabelledToggle.vue";
import { useSettingsStore } from "@/store";
import useUserInfo from "@/composables/auth/useUserInfo";
import { Sizing_Pumping_Type_Enum } from "gql/api";
import type { Bolt_On_Discharges } from "gql/api";
import { orderScenariosBySizingOrder } from "@/utils/scenarioUtils";
import { useBoltOnDischarges } from "@/composables/equipment/boltOnDischarges/boltOnDischarges";
import {
  findAutomaticDischargeTopPump,
  findBestMatchingDischarge
} from "@/models/equipment/bolt_on_discharges/properties";
import {
  collectReportParityMetrics,
  SIZING_PARITY_METRICS
} from "@/serverSizing/parityMetrics";

const toaster = useToast();
const { flags } = useSettingsStore();

const sizing = injectStrict(sizingKey);
const sizingForm = injectStrict(sizingFormKey, null);
const scenarios = computed(() => sizing.value?.scenarios || []);
const orderedScenarios = computed(() =>
  orderScenariosBySizingOrder(sizing.value, scenarios.value)
);

const motorPoles = computed(() => {
  const uniquePoleData = scenarios.value.reduce<number[]>(
    (accumulator, scenario) => {
      const pole = scenario.equipment?.r_motor?.poles;
      if (pole && !accumulator.includes(pole)) {
        accumulator.push(pole);
      }
      // acc ensures uniqueness
      return accumulator;
    },
    []
  );

  return uniquePoleData;
});

const isEspOnShore = computed(() => {
  return (
    sizingForm?.data.value.pumping_type === Sizing_Pumping_Type_Enum.EspOnShore
  );
});

const allScenarioIds = computed(
  () => orderedScenarios.value?.map(s => s.id) ?? []
);
const report = ref<HTMLDivElement>();
const enablePrint = ref(true);

// Same catalog the live Parameters page auto-matches against, filtered for
// this sizing's customer.
const { customerEligibleRows: boltOnDischargeCatalog } =
  useBoltOnDischarges(false);
usePrintReflowFix(report);

const frequencyRange = ref<[number, number]>([
  flags.value.minimumPumpChartFrequency,
  flags.value.maximumPumpChartFrequency
]);

const reportSections = reactive<SizingReportSections>({
  designSchematic: {
    title: "Design Schematic",
    enabledScenarios: new Set<string>(),
    sagd: false,
    hpsHidden: true
  },
  designOverview: {
    title: "Design Overview",
    enabledScenarios: new Set<string>()
  },
  multiFrequencyHead: {
    title: "Multifrequency Head",
    enabledScenarios: new Set<string>(),
    startUpReport: true,
    hpsHidden: true
  },
  multiFrequencyBHP: {
    title: "Multifrequency BHP",
    enabledScenarios: new Set<string>(),
    startUpReport: true,
    hpsHidden: true
  },
  individualPumpCurve: {
    title: "Individual Pump Curve",
    enabledScenarios: new Set<string>()
  },
  individualPumpNPSHCurve: {
    title: "Individual Pump NPSH Curve",
    enabledScenarios: new Set<string>(),
    espHidden: true
  },
  multiFrequency: {
    title: "Multifrequency",
    enabledScenarios: new Set<string>(),
    espHidden: true
  },
  multiscenarioPumpCurve: {
    title: "Multiscenario Pump Curve",
    enabledScenarios: new Set<string>()
  },
  multiFrequencyPump: {
    title: "Multifrequency Tapered Pumps",
    enabledScenarios: new Set<string>()
  },
  pumpSpeedTorque: {
    title: "Speed - Torque",
    enabledScenarios: new Set<string>(),
    espHidden: true
  },
  motorPerformance: {
    title: "Motor Performance",
    enabledScenarios: new Set<string>(),
    hpsHidden: true
  },
  gasCurve: {
    title: "Gas Curve",
    enabledScenarios: new Set<string>(),
    showPumpThresholds: false,
    hpsHidden: true
  },
  cableTemperature: {
    title: "Cable Temperature",
    enabledScenarios: new Set<string>(),
    hpsHidden: true
  },
  pressureTraverse: {
    title: "Pressure Traverse",
    enabledScenarios: new Set<string>(),
    hpsHidden: true
  },
  IPRGraph: {
    title: "IPR",
    enabledScenarios: new Set<string>(),
    hpsHidden: true
  },
  deviationSurveyDepth: {
    title: "Deviation Surveys",
    enabledScenarios: new Set<string>(),
    hpsHidden: true
  },
  PVTCorrelationList: {
    title: "PVT Correlations",
    enabledScenarios: new Set<string>()
  },
  crossSection: {
    title: "Cross Section",
    enabledScenarios: new Set<string>(),
    hpsHidden: true
  },
  vsdResults: {
    title: "VSD",
    enabledScenarios: new Set<string>()
  },
  bendingStressAnalysis: {
    title: "Bending Stress Analysis",
    enabledScenarios: new Set<string>(),
    hpsHidden: true
  }
});

const user = useUserInfo();

const updateEnabledScenarios = (
  section: ReportSection,
  e: MultiSelectAllChangeEvent | string[]
) => {
  if (Array.isArray(e)) {
    section.enabledScenarios = new Set(e);
  }
};

const includeCoverPage = ref(true);

const scenarioMap = computed<ScenarioMap>(() =>
  orderedScenarios.value.reduce((acc, s) => {
    const {
      scenario,
      correlations,
      equipment,
      parameters,
      wellProperties,
      deviationSurvey,
      intakes,
      pumps,
      housings,
      cables,
      seals,
      yToolBypassTubings,
      yToolEquipmentTubings
    } = useDestructureScenario(s);

    const rawCalcs = useScenarioCalculations({
      scenario,
      equipment,
      wellProperties,
      parameters,
      correlations,
      pumps,
      seals,
      intakes,
      housings,
      cables,
      deviationSurvey,
      yToolBypassTubings,
      yToolEquipmentTubings
    });

    // ESP scenarios auto-match a bolt-on discharge live on Parameters and the
    // Schematic (useAutomaticBoltOnDischarge.ts), but that match is
    // deliberately never persisted just from opening a scenario, so an
    // untouched ESP scenario has no bolt_on_discharge_id/r_bolt_on_discharge
    // in the database. This report otherwise reads that persisted equipment
    // as-is, so it would show nothing for such scenarios even though
    // Parameters/Schematic show the match. This derives the same match,
    // read-only, purely for this report's display: it never writes to any
    // form or persists anything, so it carries none of the dirty-on-open risk
    // the live derivation guards against. HPS discharges are always explicit,
    // persisted picks, so this does nothing for HPS, and a legacy-imported
    // sizing keeps whatever equipment it was imported with, same as the live
    // page.
    const isHps = scenario.value?.pumping_type === Sizing_Pumping_Type_Enum.Hps;
    const isImportedFromLegacy =
      sizing.value?.journal_author_id === "import-user";
    const currentEquipment = equipment.value;
    const hasPersistedBoltOnDischarge = Boolean(
      currentEquipment?.r_bolt_on_discharge ||
        currentEquipment?.bolt_on_discharge_id
    );

    let derivedBoltOnDischarge: Bolt_On_Discharges | null = null;
    if (
      currentEquipment &&
      !isHps &&
      !isImportedFromLegacy &&
      !hasPersistedBoltOnDischarge
    ) {
      const topPump = findAutomaticDischargeTopPump(
        currentEquipment.pumps_order,
        pumps.value
      );
      const tubingOuterDiameterInches =
        rawCalcs.tubingOuterDiameterAtPumpDischargeIn.value;
      const hasPrerequisites =
        !!topPump?.id &&
        Number(topPump.housing_outer_diameter_inches) > 0 &&
        Number(tubingOuterDiameterInches) > 0 &&
        Boolean(boltOnDischargeCatalog.value?.length);

      if (hasPrerequisites) {
        derivedBoltOnDischarge = findBestMatchingDischarge(
          topPump,
          tubingOuterDiameterInches,
          boltOnDischargeCatalog.value
        );
      }
    }

    // Only rebuild equipment/calcs when a display-only discharge was actually
    // derived, so scenarios with a persisted match, HPS scenarios and legacy
    // imports pay no extra cost.
    const effectiveEquipment =
      derivedBoltOnDischarge && currentEquipment
        ? {
            ...currentEquipment,
            r_bolt_on_discharge: derivedBoltOnDischarge,
            bolt_on_discharge_id: derivedBoltOnDischarge.id
          }
        : currentEquipment;

    const calcs = derivedBoltOnDischarge
      ? useScenarioCalculations({
          scenario,
          equipment: computed(() => effectiveEquipment),
          wellProperties,
          parameters,
          correlations,
          pumps,
          seals,
          intakes,
          housings,
          cables,
          deviationSurvey,
          yToolBypassTubings,
          yToolEquipmentTubings
        })
      : rawCalcs;

    if (scenario) {
      return Object.assign(acc, {
        [scenario.value?.id]: {
          scenario: scenario.value,
          correlations: correlations.value,
          equipment: effectiveEquipment,
          parameters: parameters.value,
          wellProperties: wellProperties.value,
          deviationSurvey: deviationSurvey.value,
          intakes: intakes.value,
          pumps: pumps.value,
          housings: housings.value,
          cables: cables.value,
          seals: seals.value,
          calcs
        }
      });
    }

    return acc;
  }, {})
);

const scenarioOptions = computed(() => {
  return Object.values(scenarioMap.value).map(s => ({
    id: s.scenario?.id,
    title: s.scenario?.title
  }));
});

const liveParityPayload = computed(() => ({
  metrics: SIZING_PARITY_METRICS,
  scenarios: Object.fromEntries(
    Object.entries(scenarioMap.value).map(([scenarioId, scenario]) => [
      scenarioId,
      {
        scenarioId,
        sizingId: sizing.value?.id,
        metrics: collectReportParityMetrics(scenario.calcs)
      }
    ])
  )
}));

const scenarioSelectorItems = computed(() => {
  return [
    ...Object.values(scenarioMap.value).map(s => ({
      label: `Add ${s.scenario?.title}`,
      command: () => enableScenarios([s.scenario?.id])
    })),
    {
      label: "Clear",
      command: () => enableScenarios([])
    }
  ];
});

const enableScenarios = (scenarioIds: string[]) => {
  const hidingBlocked = (section: ReportSection) =>
    (isEspOnShore.value && section.espHidden) ||
    (!isEspOnShore.value && section.hpsHidden);

  Object.values(reportSections).forEach(section => {
    if (scenarioIds.length === 0 || hidingBlocked(section)) {
      section.enabledScenarios = new Set();
      return;
    }
    scenarioIds.forEach(id => section.enabledScenarios.add(id));
  });
};

const removeScenario = (
  scenarioId: string,
  sectionId: keyof SizingReportSections
) => {
  reportSections[sectionId].enabledScenarios.delete(scenarioId);
};

// Commenting this out since it's usage is also commented out
// function limitMaxScenarios(
//   sectionName: keyof SizingReportSections,
//   maxScenarios = 3
// ) {
//   if (reportSections[sectionName].enabledScenarios.size <= maxScenarios) return;

//   const scenarioToDelete = [
//     ...reportSections[sectionName].enabledScenarios
//   ].pop();

//   if (scenarioToDelete) {
//     removeScenario(scenarioToDelete, sectionName);
//   }

//   toaster.add({
//     severity: "warn",
//     life: 6000,
//     summary: "Report Warning",
//     detail: `Please limit the number of scenarios enabled in ${reportSections[sectionName].title} to ${maxScenarios}`
//   });
// }

watch(
  [includeCoverPage, reportSections, isEspOnShore],
  () => {
    enablePrint.value =
      includeCoverPage.value ||
      filteredReports.value.some(
        ({ section }) => section.enabledScenarios.size > 0
      );
  },
  { deep: true }
);

// limit checked scenarios in each section to 3
watch(reportSections, () => {
  // limitMaxScenarios("designOverview");
  // limitMaxScenarios("multiscenarioPumpCurve");
  enablePrint.value = false;

  for (const sectionName in reportSections) {
    const reportSection =
      reportSections[sectionName as keyof SizingReportSections];

    if (reportSection.enabledScenarios.size > 0 || includeCoverPage.value) {
      enablePrint.value = true;
    }

    if (reportSection.enabledScenarios.size <= 3) continue;

    const scenariosToDelete = [...reportSection.enabledScenarios].slice(3);

    scenariosToDelete.forEach(scenarioToDelete => {
      removeScenario(
        scenarioToDelete,
        sectionName as keyof SizingReportSections
      );

      // if multiple were added, it was from
      // add all scenarios and don't pop toasts
      if (scenariosToDelete.length === 1) {
        toaster.add({
          severity: "warn",
          life: 6000,
          summary: "Report Warning",
          detail: `Please limit the number of scenarios enabled in ${reportSection.title} to 3`
        });
      }
    });
  }
});

const filteredReports = computed(() => {
  return Object.entries(reportSections)
    .filter(([, section]) => {
      const passesAdminRule = !section.requiresAdmin || user.is_admin;
      const passesModeRule = isEspOnShore.value
        ? !section.espHidden
        : !section.hpsHidden;
      return passesAdminRule && passesModeRule;
    })
    .map(([key, section]) => ({
      key: key as keyof SizingReportSections,
      section
    }));
});

const onPrint = async () => {
  await nextTick();
  window.print();
};

const ddvTickFrequencies = reactive({ x: ".6in", y: ".3in" });
provide("ddvTickFrequencies", ddvTickFrequencies);
</script>

<template>
  <div class="container report-grid">
    <section v-bind="$attrs">
      <div v-if="sizing && scenarioMap" class="noprint">
        <div
          class="mb-4 flex align-items-center justify-content-between flex-wrap"
        >
          <h1>Reports</h1>
          <div
            class="flex align-items-center justify-content-between flex-wrap"
          >
            <span class="flex-shrink-0 mr-1">Operating Range:</span>
            <div class="flex align-items-center">
              <PumpFrequencyRange
                v-model.lazy="frequencyRange"
                :motor-poles="motorPoles"
              />
            </div>
          </div>
        </div>
        <div class="mb-2 flex gap-2 align-items-center justify-content-between">
          <SplitButton
            class="p-button-success"
            label="Add All Scenarios"
            :model="scenarioSelectorItems"
            :pt="{ menu: { action: { 'aria-hidden': false } } }"
            @click="enableScenarios(allScenarioIds)"
          />
          <Button
            class="p-button-info"
            icon="fas fa-print"
            label="Print Report"
            :disabled="!enablePrint"
            @click="onPrint"
          />
        </div>
        <div class="field-checkbox">
          <Checkbox
            v-model="includeCoverPage"
            input-id="includeCoverPage"
            :binary="true"
          />
          <label for="binary">Include cover page?</label>
        </div>
        <div class="report-multiselect-container">
          <div v-for="{ section, key } in filteredReports" :key="section.title">
            <div>
              <span class="label">{{ section.title }}</span>
            </div>
            <MultiSelect
              :modelValue="[...section.enabledScenarios]"
              placeholder="Click to add scenarios..."
              scroll-height="50vh"
              option-label="title"
              option-value="id"
              :disabled="!scenarioMap"
              :options="section.options || scenarioOptions"
              @update:model-value="updateEnabledScenarios(section, $event)"
            >
              <template #value="{ value, placeholder }">
                <div v-if="value.length" class="flex flex-wrap gap-1">
                  <template v-for="id in value" :key="id">
                    <Chip
                      :label="scenarioMap[id].scenario?.title || ''"
                      removable
                      :pt="{
                        removeIcon: {
                          label: `${scenarioMap[id].scenario?.title || ''}`
                        }
                      }"
                      @remove="removeScenario(id, key)"
                    />
                  </template>
                </div>
                <div v-else>
                  <span>{{ placeholder }}</span>
                </div>
              </template>
            </MultiSelect>

            <div class="my-1 ml-1 flex align-items-center">
              <LabelledToggle
                v-if="'sagd' in section && section.enabledScenarios.size"
                v-model="section.sagd"
                text="SAGD"
                class="my-1 ml-1 flex align-items-center"
              />
            </div>
            <LabelledToggle
              v-if="
                'showPumpThresholds' in section && section.enabledScenarios.size
              "
              v-model="section.showPumpThresholds"
              text="Show Pump Thresholds"
            />
          </div>
        </div>
      </div>
    </section>

    <Report
      id="report"
      class="report"
      :frequency-range="frequencyRange"
      :report-sections="reportSections"
      :scenario-map="scenarioMap"
      :sizing="sizing"
      :include-cover-page="includeCoverPage"
    />
    <pre
      data-testid="sizing-live-parity-payload"
      hidden
      v-text="JSON.stringify(liveParityPayload)"
    />
  </div>
</template>

<style lang="scss" scoped>
:deep() {
  * {
    text-rendering: optimizeLegibility !important;
  }
}
@media screen {
  #report {
    position: sticky;
    top: 1em;
    margin: 1em;
    padding: 1em;
    border: 5px dashed var(--surface-300);
    zoom: 0.8;
    height: 11in;
    overflow-y: scroll;
  }
  .report {
    size: letter portrait;
    width: 8.5in;

    &.report-preview {
      zoom: 0.8;
      height: 11in;
      overflow-x: auto;
      overflow-y: scroll;
    }
  }

  .report-grid {
    display: grid;
    grid-template-columns: 1fr 1fr;
    padding: 1em;
  }

  .report-multiselect-container {
    display: grid;
    grid-template-columns: 1fr;
    grid-auto-flow: dense;
    gap: 1em;

    :deep(.p-multiselect) {
      width: 100%;
      max-width: 300px;

      @include bp("desktop") {
        max-width: 300px;
      }

      @include bp("hd") {
        max-width: 500px;
      }

      @include bp("uhd") {
        max-width: 800px;
      }
    }
  }
}

@media print {
  .report-grid > section {
    display: none !important;
  }

  #report {
    position: initial;
    display: block;
    width: 100%;
    zoom: 1;
    border: none;
    margin: 0;
    padding: 0;
    visibility: visible;
    height: unset;
    overflow: visible;
  }

  .report-grid {
    display: block;
    padding: 0;
  }
}
</style>
